import React, { useState } from 'react';
import { ArrowLeft, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface Salario {
  id: string;
  funcionario: string;
  cargo: string;
  valorBruto: number;
  descontoINSS?: number;
  descontoIRPF?: number;
  outrosDescontos?: number;
  salarioLiquido: number;
  dataVencimento: number; // dia do mês (1-31)
  status: 'pago' | 'pendente' | 'atrasado';
  observacoes?: string;
  ativo: boolean;
  dataInicio: string;
  dataFim?: string;
  tipoFundoGarantia?: 'FGTS' | 'FGTM';
  calcularINSS?: boolean;
  calcularIRPF?: boolean;
  // Manter compatibilidade
  valor?: number;
}

const SalariosNovoPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    funcionario: '',
    cargo: '',
    valorBruto: '',
    outrosDescontos: '',
    dataVencimento: '',
    observacoes: '',
    ativo: true,
    dataInicio: new Date().toISOString().split('T')[0],
    tipoFundoGarantia: 'FGTS' as 'FGTS' | 'FGTM',
    calcularINSS: false,
    calcularIRPF: false
  });

  const calcularDescontos = (valorBruto: number, calcularINSS: boolean, calcularIRPF: boolean) => {
    let descontoINSS = 0;
    let descontoIRPF = 0;

    if (calcularINSS) {
      // Cálculo simplificado do INSS (2024)
      if (valorBruto <= 1412) {
        descontoINSS = valorBruto * 0.075;
      } else if (valorBruto <= 2666.68) {
        descontoINSS = valorBruto * 0.09;
      } else if (valorBruto <= 4000.03) {
        descontoINSS = valorBruto * 0.12;
      } else {
        descontoINSS = valorBruto * 0.14;
      }
    }

    if (calcularIRPF) {
      // Cálculo simplificado do IRPF (2024)
      const baseCalculo = valorBruto - descontoINSS;
      if (baseCalculo > 2259.20) {
        if (baseCalculo <= 2826.65) {
          descontoIRPF = (baseCalculo * 0.075) - 169.44;
        } else if (baseCalculo <= 3751.05) {
          descontoIRPF = (baseCalculo * 0.15) - 381.44;
        } else if (baseCalculo <= 4664.68) {
          descontoIRPF = (baseCalculo * 0.225) - 662.77;
        } else {
          descontoIRPF = (baseCalculo * 0.275) - 896.00;
        }
        descontoIRPF = Math.max(0, descontoIRPF);
      }
    }

    return { descontoINSS, descontoIRPF };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const valorBruto = parseFloat(formData.valorBruto);
    const outrosDescontos = parseFloat(formData.outrosDescontos) || 0;
    const { descontoINSS, descontoIRPF } = calcularDescontos(valorBruto, formData.calcularINSS, formData.calcularIRPF);
    const salarioLiquido = valorBruto - descontoINSS - descontoIRPF - outrosDescontos;

    const novoSalario: Omit<Salario, 'id'> = {
      funcionario: formData.funcionario,
      cargo: formData.cargo,
      valorBruto,
      descontoINSS: formData.calcularINSS ? descontoINSS : undefined,
      descontoIRPF: formData.calcularIRPF ? descontoIRPF : undefined,
      outrosDescontos: outrosDescontos > 0 ? outrosDescontos : undefined,
      salarioLiquido,
      dataVencimento: parseInt(formData.dataVencimento),
      status: 'pendente',
      observacoes: formData.observacoes,
      ativo: formData.ativo,
      dataInicio: formData.dataInicio,
      tipoFundoGarantia: formData.tipoFundoGarantia,
      calcularINSS: formData.calcularINSS,
      calcularIRPF: formData.calcularIRPF
    };

    // TODO: Implementar salvamento real (API/store)
    console.log('Novo salário:', novoSalario);
    
    // Navegar de volta para a página de salários
    navigate('/financas/salarios');
  };

  const handleCancel = () => {
    navigate('/financas/salarios');
  };

  return (
    <div className="min-h-screen bg-background dark:bg-background -mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-12">
      <div className="px-4 sm:px-6 lg:px-8 xl:px-12 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div className="flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold text-foreground dark:text-foreground">
              Cadastrar Novo Salário
            </h1>
          </div>
        </div>
      </div>

      {/* Form Card - Largura total */}
      <Card className="mx-4 sm:mx-6 lg:mx-8 xl:mx-12">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Informações do Salário
          </CardTitle>
        </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Funcionário e Cargo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="funcionario">Funcionário</Label>
                  <Input
                    id="funcionario"
                    value={formData.funcionario}
                    onChange={(e) => setFormData({...formData, funcionario: e.target.value})}
                    placeholder="Nome do funcionário"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="cargo">Cargo</Label>
                  <Input
                    id="cargo"
                    value={formData.cargo}
                    onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                    placeholder="Cargo do funcionário"
                    required
                  />
                </div>
              </div>

              {/* Valor Bruto */}
              <div>
                <Label htmlFor="valorBruto">Valor Bruto (R$)</Label>
                <Input
                  id="valorBruto"
                  type="number"
                  step="0.01"
                  value={formData.valorBruto}
                  onChange={(e) => setFormData({...formData, valorBruto: e.target.value})}
                  placeholder="0,00"
                  required
                />
              </div>

              {/* Tipo do Fundo de Garantia */}
              <div>
                <Label htmlFor="tipoFundoGarantia">Tipo do Fundo de Garantia</Label>
                <Select value={formData.tipoFundoGarantia} onValueChange={(value: 'FGTS' | 'FGTM') => setFormData({...formData, tipoFundoGarantia: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FGTS">FGTS (Fundo de Garantia por Tempo de Serviço)</SelectItem>
                    <SelectItem value="FGTM">FGTM (Fundo de Garantia por Tempo Ministerial)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Checkboxes para cálculos */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="calcularINSS"
                    checked={formData.calcularINSS}
                    onChange={(e) => setFormData({...formData, calcularINSS: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="calcularINSS">Adicionar cálculo do INSS</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="calcularIRPF"
                    checked={formData.calcularIRPF}
                    onChange={(e) => setFormData({...formData, calcularIRPF: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="calcularIRPF">Adicionar cálculo IRPF</Label>
                </div>
              </div>

              {/* Outros Descontos */}
              <div>
                <Label htmlFor="outrosDescontos">Outros Descontos (R$)</Label>
                <Input
                  id="outrosDescontos"
                  type="number"
                  step="0.01"
                  value={formData.outrosDescontos}
                  onChange={(e) => setFormData({...formData, outrosDescontos: e.target.value})}
                  placeholder="0,00"
                />
              </div>

              {/* Data de Vencimento e Data de Início */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dataVencimento">Dia do Vencimento</Label>
                  <Select value={formData.dataVencimento} onValueChange={(value) => setFormData({...formData, dataVencimento: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o dia" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                        <SelectItem key={day} value={day.toString()}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="dataInicio">Data de Início</Label>
                  <Input
                    id="dataInicio"
                    type="date"
                    value={formData.dataInicio}
                    onChange={(e) => setFormData({...formData, dataInicio: e.target.value})}
                    required
                  />
                </div>
              </div>

              {/* Observações */}
              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Input
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Observações adicionais"
                />
              </div>

              {/* Checkbox Ativo */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="ativo">Salário ativo (pagamento recorrente)</Label>
              </div>

              {/* Botões */}
              <div className="flex gap-4 pt-6">
                <Button type="button" variant="outline" onClick={handleCancel} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1">
                  Cadastrar Salário
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
    </div>
  );
};

export default SalariosNovoPage;
export { SalariosNovoPage };